import { useState, useCallback, useRef, useEffect } from 'react';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getClientX = event => {
  const touch = event.touches?.[0] || event.changedTouches?.[0];
  return touch ? touch.clientX : event.clientX;
};

/**
 * Independent, spreadsheet-style column widths for a table, persisted in
 * localStorage under storageKey so different tables (e.g. Data vs Pro) don't
 * share widths. Each column is identified by its own key (e.g. 'reason',
 * 'condition1') and resizes independently of the others.
 *
 * A column with no stored width is left completely alone (getColumnWidth
 * returns undefined, so no width/maxWidth style is applied) so it keeps its
 * normal auto-sized-to-content look. An explicit width only ever gets
 * applied once the user has actually dragged that column.
 *
 * `enabled` (default true) is the manual-resize on/off switch: while false,
 * getColumnWidth always returns undefined (auto-sizing, regardless of any
 * stored width) and startResize returns undefined instead of a handler, so
 * ColumnResizeHandle renders nothing at all. Nothing gets cleared from
 * storage - turning it back on picks the stored widths back up unchanged.
 */
export const useColumnWidths = (storageKey, { min = 100, max = 800, enabled = true } = {}) => {
  const [widths, setWidths] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '{}');
    } catch {
      return {};
    }
  });
  const widthsRef = useRef(widths);
  useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  // Clamp on every read, not just on write - a width stored before `min` was
  // raised (or before this resize feature had its bugs fixed) could still be
  // sitting in localStorage below a column's actual content-forced minimum
  // (e.g. a button label like "reason" or "condition" won't render narrower
  // than ~70-85px no matter what width we ask for, since these tables don't
  // use table-layout: fixed). Without this, such a column could look like
  // resizing does nothing: the drag math would keep starting from the old
  // tiny stored value, so small drags would never visibly change anything.
  const getColumnWidth = useCallback(
    columnKey => (enabled && columnKey in widths ? clamp(widths[columnKey], min, max) : undefined),
    [widths, min, max, enabled]
  );

  const startResize = useCallback(columnKey => {
    if (!enabled) return undefined;

    return event => {
      event.preventDefault();
      const startX = getClientX(event);

      // First-ever drag on a column that's never been customized: start from
      // its actual current on-screen width (however the browser auto-sized
      // it), not an arbitrary constant - otherwise the column would jump to
      // that constant's size the instant you touch the handle.
      const stored = widthsRef.current[columnKey];
      const cell = event.target.closest && event.target.closest('td, th');
      const measuredWidth = cell ? cell.getBoundingClientRect().width : min;
      const startWidth = stored !== undefined ? clamp(stored, min, max) : measuredWidth;

      const handleMove = moveEvent => {
        moveEvent.preventDefault();
        const nextWidth = clamp(startWidth + (getClientX(moveEvent) - startX), min, max);
        setWidths(prev => ({ ...prev, [columnKey]: nextWidth }));
      };

      const handleEnd = () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
        window.removeEventListener('touchcancel', handleEnd);
        localStorage.setItem(storageKey, JSON.stringify(widthsRef.current));
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      window.addEventListener('touchcancel', handleEnd);
    };
  }, [storageKey, min, max, enabled]);

  return { getColumnWidth, startResize };
};

export default useColumnWidths;
